import React, { useState, useEffect, useRef } from "react";
import jsPDF from "jspdf";

const FloorplanPrintModal = ({ date, onClose, mobileMode = false }) => {
  const [tables, setTables] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [floor, setFloor] = useState("inside");
  const [period, setPeriod] = useState("evening");
  const canvasRef = useRef(null);
  const [previewReady, setPreviewReady] = useState(false);
  
  const dateStr = date.toLocaleDateString("en-CA");
  const dateLabel = date.toLocaleDateString("hr-HR", { day: "numeric", month: "long", year: "numeric" });

  const PERIODS = {
    morning:   { label: "Jutro",         start: 8,  end: 12 },
    afternoon: { label: "Poslijepodne",  start: 12, end: 20 },
    evening:   { label: "Večer",         start: 20, end: 32 },
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [tablesRes, resRes] = await Promise.all([
        fetch(`/reservations/api/tables/?date=${dateStr}`).then(r => r.json()),
        fetch(`/reservations/api/reservations/by-date/?date=${dateStr}`).then(r => r.json()),
      ]);
      setTables(tablesRes.tables || []);
      setReservations(resRes.reservations || []);
      setLoading(false);
    };
    fetchData();
  }, [dateStr]);

  const getReservationsForPeriod = (tableLabel) => {
    const p = PERIODS[period];
    return reservations.filter(r => {
      if (!r.tables.includes(tableLabel)) return false;
      if (!r.start_datetime) return false;
      const [h, m] = r.start_datetime.split(":").map(Number);
      const totalH = h + m / 60;
      if (period === "evening") {
        return totalH >= 20 || totalH < 8;
      }
      return totalH >= p.start && totalH < p.end;
    });
  };

  const filteredTables = tables.filter(t =>
    floor === "inside" ? !t.is_outdoor : t.is_outdoor
  );

  const imgSrc = floor === "inside" ? "/tlocrt_inside.png" : "/tlocrt_outside.png";

  useEffect(() => {
    if (loading || !canvasRef.current) return;
    setPreviewReady(false);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const img = new Image();
    img.src = imgSrc;
    img.onload = () => {
      const TITLE_H = 20;
      const CANVAS_W = img.naturalWidth;
      const CANVAS_H = img.naturalHeight + TITLE_H;
      canvas.width = CANVAS_W;
      canvas.height = CANVAS_H;

      const offsetX = 0;
      const offsetY = TITLE_H;
      const scale = 1;

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      ctx.drawImage(img, offsetX, offsetY, img.naturalWidth, img.naturalHeight);

      ctx.fillStyle = "#000000";
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.fillText(
        `The House Pub – ${floor === "inside" ? "Unutarnji" : "Vanjski"} – ${PERIODS[period].label} – ${dateLabel}`,
        CANVAS_W / 2, 14
      );

      filteredTables.forEach(t => {
        const x = offsetX + t.position_x * scale;
        const y = offsetY + t.position_y * scale;
        const w = t.width * scale;
        const h = t.height * scale;

        const resas = getReservationsForPeriod(t.label);
        const hasResa = resas.length > 0;

        const bgColor = hasResa ? "#c1b428" : "#16a34a";
        const isRound = t.type === "round" || t.type === "bar_stool";

        ctx.fillStyle = bgColor;
        ctx.strokeStyle = "#333333";
        ctx.lineWidth = 1;

        if (isRound) {
          ctx.beginPath();
          ctx.ellipse(x + w/2, y + h/2, w/2, h/2, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        } else {
          const radius = 4;
          ctx.beginPath();
          ctx.moveTo(x + radius, y);
          ctx.lineTo(x + w - radius, y);
          ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
          ctx.lineTo(x + w, y + h - radius);
          ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
          ctx.lineTo(x + radius, y + h);
          ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
          ctx.lineTo(x, y + radius);
          ctx.quadraticCurveTo(x, y, x + radius, y);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        }

        ctx.fillStyle = "#000000";
        ctx.textAlign = "center";

        if (hasResa) {
          const names = resas.map(r => r.guest_name).join(", ");
          const isBarStool = t.type === "bar_stool";

          if (isBarStool) {
            ctx.font = `bold 8px Arial`;
            ctx.strokeStyle = "#000000";
            ctx.lineWidth = 2;
            const textW = ctx.measureText(names).width + 4;
            ctx.fillStyle = "rgba(0,0,0,0.7)";
            ctx.fillRect(x + w/2 - textW/2, y - 14, textW, 12);
            ctx.fillStyle = "#ffffff";
            ctx.strokeText(names, x + w/2, y - 4);
            ctx.fillText(names, x + w/2, y - 4);
          } else {
            ctx.fillStyle = "#000000";
            const words = names.split(" ");
            const lines = [];
            let currentLine = "";
            let baseFontSize = Math.max(8, Math.min(12, w * 0.18));
            ctx.font = `bold ${baseFontSize}px Arial`;

            for (const word of words) {
              const testLine = currentLine ? currentLine + " " + word : word;
              if (ctx.measureText(testLine).width > w - 6 && currentLine) {
                lines.push(currentLine);
                currentLine = word;
              } else {
                currentLine = testLine;
              }
            }
            if (currentLine) lines.push(currentLine);

            const lineHeight = baseFontSize + 2;
            const totalTextH = lines.length * lineHeight;
            if (totalTextH > h - 4) {
              baseFontSize = Math.max(6, Math.floor((h - 4) / lines.length) - 2);
              ctx.font = `bold ${baseFontSize}px Arial`;
            }

            lines.forEach((line) => {
              if (ctx.measureText(line).width > w - 4) {
                const fitFontSize = Math.max(6, Math.floor(baseFontSize * (w - 4) / ctx.measureText(line).width));
                baseFontSize = Math.min(baseFontSize, fitFontSize);
              }
            });
            ctx.font = `bold ${baseFontSize}px Arial`;

            const finalLineHeight = baseFontSize + 2;
            const finalTotalH = lines.length * finalLineHeight;
            const startY = y + h / 2 - finalTotalH / 2 + finalLineHeight / 2;
            lines.forEach((l, i) => {
              ctx.fillText(l, x + w / 2, startY + i * finalLineHeight);
            });
          }
        } else {
          const fontSize = Math.max(7, Math.min(10, w / 3));
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.fillText(t.label, x + w / 2, y + h / 2 + fontSize / 3);
        }
      });

      setPreviewReady(true);
    };
  }, [loading, floor, period, filteredTables, reservations]);

  const handleDownloadJPG = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = `tlocrt_${floor}_${period}_${dateStr}.jpg`;
    link.href = canvas.toDataURL("image/jpeg", 0.95);
    link.click();
  };

  const handleDownloadPDF = () => {
    const canvas = canvasRef.current;
    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [canvas.width, canvas.height] });
    pdf.addImage(imgData, "JPEG", 0, 0, canvas.width, canvas.height);
    pdf.save(`tlocrt_${floor}_${period}_${dateStr}.pdf`);
  };

  const handlePrint = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Tlocrt – ${dateLabel}</title>
        <style>
          @page { size: A4 landscape; margin: 0; }
          body { margin: 0; padding: 0; }
          img { width: 100%; height: auto; display: block; }
        </style>
      </head>
      <body>
        <img src="${dataUrl}" />
        <script>
          window.onload = () => setTimeout(() => { window.print(); }, 300);
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleShareWhatsApp = () => {
    if (navigator.share) {
      canvasRef.current.toBlob(blob => {
        const file = new File([blob], `tlocrt_${dateStr}.jpg`, { type: "image/jpeg" });
        navigator.share({
          title: "Tlocrt rezervacija",
          text: `The House Pub – ${dateLabel}`,
          files: [file],
        }).catch(() => {});
      }, "image/jpeg", 0.95);
    } else {
      alert("Dijeljenje nije podržano na ovom uređaju/browseru.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex flex-col" style={{ zIndex: 200 }}>
      
      {/* Header */}
      <div className="bg-gray-900 border-b border-[#c1b428] px-4 py-3 flex justify-between items-center flex-shrink-0">
        <h2 className="text-[#c1b428] font-bold text-base">🗺️ Tlocrt rezervacija</h2>
        <button onClick={onClose} className="text-gray-400 text-2xl leading-none">✕</button>
      </div>

      {/* Kontrole */}
      <div className="bg-gray-900 border-b border-gray-700 px-4 py-3 flex-shrink-0">
        <p className="text-gray-400 text-xs mb-2">{dateLabel}</p>
        <div className="flex gap-4 flex-wrap">
          <div>
            <p className="text-[#c1b428] text-xs font-semibold mb-1">Termin:</p>
            <div className="flex gap-2">
              {Object.entries(PERIODS).map(([key, val]) => (
                <button key={key} onClick={() => setPeriod(key)}
                  className={`px-3 py-1 rounded text-xs font-semibold border ${
                    period === key ? "bg-[#c1b428] text-black border-[#c1b428]" : "bg-gray-800 text-[#c1b428] border-gray-600"
                  }`}>
                  {val.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[#c1b428] text-xs font-semibold mb-1">Prostor:</p>
            <div className="flex gap-2">
              <button onClick={() => setFloor("inside")}
                className={`px-3 py-1 rounded text-xs font-semibold border ${
                  floor === "inside" ? "bg-[#c1b428] text-black border-[#c1b428]" : "bg-gray-800 text-[#c1b428] border-gray-600"
                }`}>
                🏠 Unutarnji
              </button>
              <button onClick={() => setFloor("outside")}
                className={`px-3 py-1 rounded text-xs font-semibold border ${
                  floor === "outside" ? "bg-[#c1b428] text-black border-[#c1b428]" : "bg-gray-800 text-[#c1b428] border-gray-600"
                }`}>
                🌿 Vanjski
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Canvas preview */}
      <div className="flex-1 overflow-auto bg-gray-950 flex items-center justify-center p-4">
        {loading ? (
          <p className="text-gray-400">Učitavam...</p>
        ) : (
          <canvas ref={canvasRef} style={{ maxWidth: "100%", maxHeight: "100%", border: "1px solid #c1b428", borderRadius: 8 }} />
        )}
      </div>

      {/* Akcije – mobileMode sakriva PDF i Print */}
      <div className="bg-gray-900 border-t border-gray-700 px-4 py-3 flex gap-2 flex-wrap flex-shrink-0">
        <button onClick={handleDownloadJPG} disabled={!previewReady}
          className="flex-1 py-2 bg-[#c1b428] text-black rounded-lg font-bold text-xs disabled:opacity-50">
          ⬇️ JPG
        </button>
        {!mobileMode && (
          <>
            <button onClick={handleDownloadPDF} disabled={!previewReady}
              className="flex-1 py-2 bg-gray-700 text-[#c1b428] border border-[#c1b428] rounded-lg font-semibold text-xs disabled:opacity-50">
              📄 PDF
            </button>
            <button onClick={handlePrint} disabled={!previewReady}
              className="flex-1 py-2 bg-gray-700 text-[#c1b428] border border-[#c1b428] rounded-lg font-semibold text-xs disabled:opacity-50">
              🖨️ Print
            </button>
          </>
        )}
        <button onClick={handleShareWhatsApp} disabled={!previewReady}
          className="flex-1 py-2 bg-green-700 text-white rounded-lg font-semibold text-xs disabled:opacity-50">
          📱 Dijeli
        </button>
        <button onClick={onClose}
          className="flex-1 py-2 bg-gray-800 text-gray-400 rounded-lg text-xs">
          Zatvori
        </button>
      </div>
    </div>
  );
};

export default FloorplanPrintModal;